import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { ReleaseNoteService } from '../../../core/data-services/release-note.service';
import { MediaScope } from '../../../shared/models/release-note/release-note.model';

/**
 * Maps `(scope, ref)` to a blob object URL for release-note media (RA-2695). The media endpoints need the
 * bearer header, so bytes are fetched through HttpClient and bound as `blob:` URLs — which Angular's URL
 * sanitizer already treats as safe, so no bypass is needed.
 *
 * The in-flight observable is cached, so one ref is fetched once per session however many components ask.
 * A failed fetch resolves to null (the caller shows its placeholder — never a banner). {@link revoke}
 * revokes the URLs; the card calls it on close, the Library on destroy, and the editor on destroy and
 * release switch.
 */
@Injectable({
  providedIn: 'root'
})
export class ReleaseNoteMediaCacheService {

  private readonly entries = new Map<string, Observable<string | null>>();
  private readonly urls = new Map<string, string>();

  constructor(private readonly releaseNoteService: ReleaseNoteService) {}

  /** The object URL for a ref, or null when it cannot be read. */
  public url(ref: string, scope: MediaScope): Observable<string | null> {
    if (!ref) {
      return of(null);
    }
    const key = this.key(scope, ref);
    if (!this.entries.has(key)) {
      const entry: Observable<string | null> = this.releaseNoteService.getMediaBlob(ref, scope).pipe(
        map(blob => {
          const url = URL.createObjectURL(blob);
          // Revoked while in flight: nobody will ever revoke this one, so do not keep it.
          if (this.entries.get(key) !== entry) {
            URL.revokeObjectURL(url);
            return null;
          }
          this.urls.set(key, url);
          return url;
        }),
        catchError(() => {
          if (this.entries.get(key) === entry) {
            this.entries.delete(key);
          }
          return of(null);
        }),
        // refCount: a fetch nobody is waiting for any more is cancelled rather than left to finish.
        shareReplay({ bufferSize: 1, refCount: true })
      );
      this.entries.set(key, entry);
    }
    return this.entries.get(key);
  }

  /**
   * Serves a just-uploaded file from the bytes the author picked. The server resolves a ref only once a saved
   * revision holds it (by design — refs are looked up, never trusted), so until the first save the editor and
   * its Preview could not otherwise show what was uploaded.
   */
  public seed(ref: string, scope: MediaScope, file: Blob): void {
    const key = this.key(scope, ref);
    const url = URL.createObjectURL(file);
    this.urls.set(key, url);
    this.entries.set(key, of(url));
  }

  /**
   * Revokes and forgets URLs: the given refs in a scope, every ref in a scope, or everything.
   *
   * Entries are one shared `scope|ref` map with no reference counting, so revoking a ref revokes it for
   * every holder — passing refs narrows what is revoked to what the caller loaded, but if another
   * component is showing the same ref, its entry goes too. That is tolerated: an `<img>` that has already
   * decoded keeps rendering, and the next `url()` call for the ref refetches.
   */
  public revoke(scope?: MediaScope, refs?: Array<string>): void {
    const prefix = scope ? `${scope}|` : '';
    const only = refs ? new Set(refs.map(ref => this.key(scope, ref))) : null;
    Array.from(this.entries.keys())
      .filter(key => key.startsWith(prefix) && (!only || only.has(key)))
      .forEach(key => {
        const url = this.urls.get(key);
        if (url) {
          URL.revokeObjectURL(url);
        }
        this.urls.delete(key);
        this.entries.delete(key);
      });
  }

  private key(scope: MediaScope, ref: string): string {
    return `${scope}|${ref}`;
  }
}
